import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { DeleteDialog } from 'ui/DeleteDialog';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import {
	deleteAnnualFeeTypeRequest,
	getAnnualFeeTypeListRequest,
} from 'redux/reducers/easFee/reducer';
import { getAnnualFeeTypeList, getAnnualFeeTypeListLoading } from 'redux/reducers/easFee/selectors';
import { TAnnualFeeTypeDetails } from 'redux/reducers/easFee/types';
import { getEmEnumsRequest } from 'redux/reducers/emManagement/reducer';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import { getEnumsSupportedCurrency } from 'redux/reducers/emManagement/selectors';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { AnnualFeeTypeDialog } from './AnnualFeeTypeDialog';
import { resolveCurrencyName } from '../utils';

const EDIT_ACTION = 'Edit';
const DELETE_ACTION = 'Delete';

export const AnnualFeeTypes: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [annualFeeTypeToEdit, setAnnualFeeTypeToEdit] = useState<TAnnualFeeTypeDetails | null>(
		null,
	);
	const [idToDelete, setIdToDelete] = useState<number | null>(null);

	const list = useSelector(getAnnualFeeTypeList);
	const listLoading = useSelector(getAnnualFeeTypeListLoading);
	const supportedCurrency = useSelector(getEnumsSupportedCurrency);

	useEffect(() => {
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SUPPORTED_CURRENCY }));
	}, [dispatch]);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				column.type === ETableStructureType.AMOUNT && !column.currency
					? {
							...column,
							currency: (item: TAnnualFeeTypeDetails) =>
								resolveCurrencyName(supportedCurrency, item.currency),
						}
					: column,
			),
		[currentTableStructure, supportedCurrency],
	);

	/** Keeps only the visibility flags, so the columns stay free of the derived render callbacks. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TAnnualFeeTypeDetails>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			getAnnualFeeTypeListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const handleAddNew = useCallback(() => {
		setAnnualFeeTypeToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				setAnnualFeeTypeToEdit(list?.data?.find((item) => item.id === id) ?? null);
				setDialogOpen(true);
			}
			if (action === DELETE_ACTION) {
				setIdToDelete(id);
			}
		},
		[list],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setAnnualFeeTypeToEdit(null);
	}, []);

	const handleDeleteClose = useCallback(() => {
		setIdToDelete(null);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		if (idToDelete !== null) {
			dispatch(deleteAnnualFeeTypeRequest({ id: idToDelete }));
		}
		setIdToDelete(null);
	}, [dispatch, idToDelete]);

	return (
		<>
			<AnnualFeeTypeDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				annualFeeTypeToEdit={annualFeeTypeToEdit}
			/>
			<DeleteDialog
				open={idToDelete !== null}
				setClose={handleDeleteClose}
				handleDelete={handleDeleteConfirm}
				text="Are you sure you want to delete this annual fee type?"
			/>
			<EasTableWithFilter<TAnnualFeeTypeDetails>
				tableStructure={structure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / Fees / <b>Non-Union Subscriptions</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add New Non-Union Subscription
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
